import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Player, PLAYERS, StatGroup, randSessionId } from '../constants/brand';
import { Gender, LeaderboardEntry } from '../models/booth.models';
import { BoothSyncService } from './booth-sync.service';
import { PairingService } from './pairing.service';
import {
  ApiService, ApiPlayer, EventTypeStat, CompetitionMode, CreateGuestRequest,
} from './api.service';
import { getGradeConfig } from '../constants/grade-config';
import { DrillConfig, mapApiDrills } from '../constants/drill-config';

// ── Convert skill_level "10.000000" (0-10 scale) → 0-100 integer ────────────
// Prefers the K-weighted per-rule value when the backend sends one (each
// business rule carries its own evaluated_events/reps, so it can be
// K-weighted independently of the drill-level and overall-level averages).
// Falls back to the raw skill_level when weighted_skill_level is absent.
function skillToStat(rule: { skill_level: string; weighted_skill_level?: string }): number {
  return parseFloat(parseFloat(rule.weighted_skill_level ?? rule.skill_level).toFixed(2));
}

// ── Map a guest's real per-drill business-rule breakdown (same raw shape as
//    the player's `stats`, from buildPlayerStats()) into StatGroup[] — no
//    fixed "Ball Handling / Defense" categories, just each drill as-is. ─────
export function mapGuestStats(stats: EventTypeStat[]): StatGroup[] {
  return (stats ?? []).map(g => ({
    eventTypeId:       g.event_type_id,
    eventTypeName:     g.event_type_name,
    averageSkillLevel: g.average_skill_level ?? 0,
    drillVideoUrl:     g.drill_video_url ?? null,
    rules: g.business_rules.map(r => ({
      name:  r.business_rule_name,
      value: skillToStat(r.current_skill_level),
    })),
  }));
}

// ── Compute average score from all stats groups ───────────────────────────────
function computeScore(stats: EventTypeStat[]): number {
  const allSkills = stats.flatMap(g => g.business_rules);
  if (!allSkills.length) return 5;
  const avg = allSkills.reduce(
    (sum, r) => sum + parseFloat(r.current_skill_level.weighted_skill_level ?? r.current_skill_level.skill_level), 0
  ) / allSkills.length;
  return parseFloat(avg.toFixed(2));
}

// ── Map API player → local Player ────────────────────────────────────────────
export function mapApiPlayer(p: ApiPlayer, gradeId: string, gender: Gender): Player {
  const genderKey = gender === 'boy' ? 'boy' : 'girl';
  const fallback  = PLAYERS[gradeId]?.[genderKey];

  const groups = p.stats ?? [];
  const g0 = groups[0]?.business_rules ?? [];
  const g1 = groups[1]?.business_rules ?? [];

  const getStat = (rules: typeof g0, idx: number) =>
    rules[idx] ? skillToStat(rules[idx].current_skill_level) : 0;

  const bh = g0.length ? {
    dribbling: getStat(g0, 0),
    passing:   getStat(g0, 1),
    catching:  getStat(g0, 2),
    offHand:   getStat(g0, 3),
  } : (fallback?.bh ?? { passing: 60, dribbling: 65, catching: 62, offHand: 55 });

  const def = g1.length ? {
    footwork:      getStat(g1, 0),
    effort:        getStat(g1, 1),
    communication: getStat(g1, 2),
    stance:        getStat(g1, 3),
  } : (fallback?.def ?? { footwork: 58, effort: 70, communication: 60, stance: 62 });

  return {
    name:  p.display_name,
    title: p.ranking_label,
    rank:  p.ranking_label,
    // Prefer the K-weighted overall — same basis as weighted_average_score
    // on the guest side (result.component.ts) — so "Beat X.XX" (shown
    // before competing) and "YOUR SCORE vs OPPONENT" (shown after) use the
    // exact same number for this player. Falls back to the raw value when
    // the backend hasn't sent the weighted field yet.
    score:          p.weighted_overall_average_skill_level ?? p.overall_average_skill_level ?? p.score_to_beat ?? computeScore(groups),
    overallAverage: p.weighted_overall_average_skill_level ?? p.overall_average_skill_level ?? 0,
    color:          p.color  ?? fallback?.color  ?? '#EE6C24',
    jersey:         p.jersey ?? fallback?.jersey ?? '00',
    matchId:        `XO-${p.id}`,
    imageUrl:       p.image_url ?? null,
    videos:         p.video_urls?.map(v => ({ id: v.id, title: v.title, url: v.url })) ?? [],
    bh,
    def,
    statGroups: groups.map(g => ({
      eventTypeId:   g.event_type_id,
      eventTypeName: g.event_type_name,
      // Same weighted-first, raw-fallback pattern as the overall score above,
      // per drill this time — matches weighted_score on the guest's drill rows.
      averageSkillLevel: g.weighted_average_skill_level ?? g.average_skill_level ?? 0,
      drillVideoUrl:     g.drill_video_url ?? null,  // video shown during reps
      rules: g.business_rules.map(r => ({
        name:  r.business_rule_name,
        value: skillToStat(r.current_skill_level),
      })),
    })),
  };
}

@Injectable({ providedIn: 'root' })
export class BoothService {
  private router  = inject(Router);
  private sync    = inject(BoothSyncService);
  private api     = inject(ApiService);
  private pairing = inject(PairingService);

  // ── Core session state ────────────────────────────────────────────────────
  gender       = signal<Gender | null>(null);
  grade        = signal<string | null>(null);
  player       = signal<Player | null>(null);
  score        = signal<number>(0);
  won          = signal<boolean>(false);
  athleteName  = signal<string>('');
  email        = signal<string>('');
  heightFt     = signal<string>('');
  heightIn     = signal<string>('');
  drillScores  = signal<number[]>([]);
  drillScoresWithNames = signal<{ event_type_name: string; score: number }[]>([]);
  assessStats = signal<StatGroup[]>([]);
  drillIds     = signal<number[]>([]);  // ordered drill IDs from API
  heightNext   = signal<string>('/stand/demo');
  showAdmin    = signal<boolean>(false);
  adminTaps    = signal<number>(0);

  // ── API catalog ───────────────────────────────────────────────────────────
  competitionModes = signal<CompetitionMode[]>([]);
  catalogLoaded    = signal<boolean>(false);
  selectedModeId   = signal<number | null>(null);
  selectedModeCode = signal<'beat_the_player' | 'free_player_assessment' | null>(null);
  selectedPlayerId = signal<number | null>(null);

  // ── Guest (the participant playing) ────────────────────────────────────────
  guestId          = signal<number | null>(null);
  guestSessionCode = signal<string | null>(null);
  guestLoading     = signal<boolean>(false);
  guestError       = signal<boolean>(false);

  // ── Drills ────────────────────────────────────────────────────────────────
  drills               = signal<DrillConfig[]>([]);
  drillsLoaded         = signal<boolean>(false);
  private drillsLoadedForMode: string | null = null;

  // ── Session IDs (display only) ──────────────────────────────────────────────
  sessionId    = signal<string>(randSessionId());
  apiSessionId = signal<string | null>(null);

  // ── Player loading state ──────────────────────────────────────────────────
  playerLoading = signal<boolean>(false);
  playerError   = signal<boolean>(false);

  // ── Leaderboard ───────────────────────────────────────────────────────────
  leaderboard = signal<LeaderboardEntry[]>([
    { name: 'Jordan T.',  score: 94, grade: '11-12', sessionId: 'XO-0001', won: true  },
    { name: 'Destiny R.', score: 88, grade: '9-10',  sessionId: 'XO-0002', won: true  },
    { name: 'Marcus W.',  score: 79, grade: '6-8',   sessionId: 'XO-0003', won: true  },
    { name: 'Aaliyah B.', score: 71, grade: '3-5',   sessionId: 'XO-0004', won: true  },
    { name: 'Devon K.',   score: 63, grade: '6-8',   sessionId: 'XO-0005', won: false },
  ]);

  private adminTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Load drills from API (per competition mode: Beat the Player vs
  //    Free Player Assessment have different drill lists). Pass `modeOverride`
  //    when the caller knows the mode but selectedModeCode isn't set locally
  //    (e.g. the TV app is a separate BoothService instance from the iPad). ──
  loadDrills(modeOverride?: string): void {
    const mode = modeOverride ?? this.selectedModeCode() ?? 'beat_the_player';
    if (this.drillsLoaded() && this.drillsLoadedForMode === mode) return;
    this.drillsLoaded.set(true);
    this.drillsLoadedForMode = mode;
    this.api.getDrills(mode).subscribe({
      next: (res) => this.drills.set(mapApiDrills(res.data)),
      error: (err: unknown) => { this.drillsLoaded.set(false); this.drillsLoadedForMode = null; console.error('Failed to load drills', err); },
    });
  }

  // ── Load competition modes ───────────────────────────────────────────────
  loadCatalog(): void {
    if (this.catalogLoaded()) return;
    this.catalogLoaded.set(true);
    this.api.getCompetitionModes().subscribe({
      next: (res) => this.competitionModes.set(res.data),
      error: (err: unknown) => { this.catalogLoaded.set(false); console.error('Failed to load modes', err); },
    });
  }

  // ── Select competition mode ───────────────────────────────────────────────
  selectMode(code: 'beat_the_player' | 'free_player_assessment'): void {
    const mode = this.competitionModes().find(m => m.code === code);
    this.selectedModeId.set(mode?.id ?? (code === 'beat_the_player' ? 1 : 2));
    this.selectedModeCode.set(code);
  }

  // ── Load player from API ──────────────────────────────────────────────────
  loadPlayer(gradeId: string, gender: Gender): void {
    const cfg = getGradeConfig(gradeId);
    this.playerLoading.set(true);
    this.playerError.set(false);
    this.player.set(null);

    this.api.getPlayerToCompete({
      gender: gender === 'boy' ? 'boys' : 'girls',
      school_grade_from_id: cfg.school_grade_from_id,
      school_grade_to_id:   cfg.school_grade_to_id,
      sport: 'Basketball',
    }).subscribe({
      next: (res) => {
        const mapped = mapApiPlayer(res.data, gradeId, gender);
        this.player.set(mapped);
        this.selectedPlayerId.set(res.data.id);
        this.playerLoading.set(false);

        // ── Notify TV as soon as player is ready ──────────────────────────
        this.sync.emit({
          type:   'player_selected',
          player: mapped,
          grade:  gradeId,
          gender: gender,
        });
      },
      error: (err: unknown) => {
        console.error('Failed to load player', err);
        this.playerLoading.set(false);
        this.playerError.set(true);
      },
    });
  }

  // ── Create guest participant ──────────────────────────────────────────────
  /** POST /stand/guest — creates the guest participant before drills start.
   *  Called from demo.component just before navigating to countdown. */
  createGuest(): void {
    const gradeId = this.grade();
    const gender  = this.gender();
    const ft   = parseInt(this.heightFt(), 10);
    const inch = parseInt(this.heightIn(), 10);
    const competitionMode = this.selectedModeCode();

    if (!gradeId || !gender || isNaN(ft) || isNaN(inch) || !competitionMode) {
      console.warn('createGuest: missing fields', { gradeId, gender, ft, inch, competitionMode });
      return;
    }

    const cfg = getGradeConfig(gradeId);
    this.guestLoading.set(true);
    this.guestError.set(false);

    // pairing_uuid lets the backend know which booth (TV) this guest belongs
    // to, so it can broadcast the result server-side once it's computed —
    // no longer dependent on this browser tab surviving until the result
    // is ready (see getGuestResult on the backend).
    const body: CreateGuestRequest & { pairing_uuid?: string } = {
      gender:               gender === 'boy' ? 'boys' : 'girls',
      school_grade_from_id: cfg.school_grade_from_id,
      school_grade_to_id:   cfg.school_grade_to_id,
      height_feet:          ft,
      height_inches:        inch,
      stand_player_id:      this.selectedPlayerId() ?? undefined,
      competition_mode:     competitionMode,
      pairing_uuid:         this.pairing.getSavedIpadUuid() ?? undefined,
    };

    this.api.createGuest(body).subscribe({
      next: (res) => {
        this.guestId.set(res.data.id);
        this.guestSessionCode.set(res.data.session_code);
        this.guestLoading.set(false);
      },
      error: (err: unknown) => {
        console.error('Failed to create guest', err);
        this.guestLoading.set(false);
        this.guestError.set(true);
      },
    });
  }

  // ── Real-time drill event ─────────────────────────────────────────────────
  sendDrillEvent(
    type: 'drill_start' | 'drill_score' | 'drill_complete',
    drillIdx: number, score?: number, total?: number
  ): void {
    const sid = this.apiSessionId();
    if (!sid) return;
    this.api.createEvent(sid, {
      type, drill_index: drillIdx, score, total_score: total, timestamp: Date.now(),
    }).subscribe({ error: (err: unknown) => console.error('Event error', err) });
  }

  // ── Close session ─────────────────────────────────────────────────────────
  closeSession(): void {
    const sid = this.apiSessionId();
    if (!sid) return;
    this.api.closeSession(sid, {
      total_score:  this.score(),
      won:          this.won(),
      drill_scores: this.drillScores(),
      athlete_name: this.athleteName() || undefined,
      email:        this.email()       || undefined,
    }).subscribe({ error: (err: unknown) => console.error('Close session error', err) });
  }

  // ── Leaderboard ───────────────────────────────────────────────────────────
  addLeaderboardEntry(e: LeaderboardEntry): void {
    this.leaderboard.set(
      [...this.leaderboard(), e].sort((a, b) => b.score - a.score).slice(0, 10)
    );
  }

  // ── Admin ─────────────────────────────────────────────────────────────────
  handleAdminTap(): void {
    if (this.adminTimer) clearTimeout(this.adminTimer);
    const n = this.adminTaps() + 1;
    if (n >= 5) { this.showAdmin.set(true); this.adminTaps.set(0); return; }
    this.adminTaps.set(n);
    this.adminTimer = setTimeout(() => this.adminTaps.set(0), 3000);
  }

  overrideScore(s: number): void {
    const p = this.player(); if (!p) return;
    this.score.set(s); this.won.set(s > p.score); this.showAdmin.set(false);
    this.sync.emit({ type: s > p.score ? 'result_win' : 'result_loss', score: s, opponentScore: p.score, player: p, won: s > p.score });
    this.router.navigate(['/stand/result']);
  }

  // ── Full reset ────────────────────────────────────────────────────────────
  reset(): void {
    this.gender.set(null); this.grade.set(null); this.player.set(null);
    this.score.set(0); this.won.set(false);
    this.athleteName.set(''); this.email.set('');
    this.heightFt.set(''); this.heightIn.set('');
    this.drillScores.set([]);
    this.assessStats.set([]);
    this.sessionId.set(randSessionId()); this.apiSessionId.set(null);
    this.selectedModeId.set(null); this.selectedModeCode.set(null);
    this.selectedPlayerId.set(null);
    this.guestId.set(null); this.guestSessionCode.set(null);
    this.guestLoading.set(false); this.guestError.set(false);
    this.playerLoading.set(false); this.playerError.set(false);
    this.showAdmin.set(false);
    this.sync.emit({ type: 'idle' });
    this.router.navigate(['/stand/home']);
  }
}
